import { useForm, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { ReactNode } from 'react'
import LanguageSwitcher from '@components/layout/LanguageSwitcher'
import ThemeSwitcher from '@components/layout/ThemeSwitcher'
import UserAvatar from '@components/UserAvatar'
import { ToastContainer } from '@components/feedback/Toast'
import { useNotificationContext } from '@contexts/NotificationContext'
import FlashToastMonitor from '@components/feedback/FlashToastMonitor'
import GlobalSearch from '@features/global-search/components/GlobalSearch'

interface AppLayoutProps {
  user: {
    email: string
    id: string
    first_name?: string | null
    last_name?: string | null
  }
  children: ReactNode
}

export default function AppLayout({ user, children }: AppLayoutProps) {
  const { delete: destroy } = useForm()
  const { t } = useTranslation()
  const { toasts, dismissToast } = useNotificationContext()

  const handleSignOut = () => {
    destroy('/session')
  }

  const displayName = user.first_name && user.last_name
    ? `${user.first_name} ${user.last_name}`
    : user.email

  return (
    <div className="min-h-screen bg-base-200 overflow-hidden">
      {/* Flash Message Monitor */}
      <FlashToastMonitor />

      {/* Notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Navbar */}
      <div className="navbar bg-base-100 shadow-lg fixed top-0 left-0 right-0 z-50 h-16">
        <div className="container mx-auto px-4 flex items-center justify-between gap-4">
          {/* Left: Logo */}
          <div className="flex-shrink-0">
            <Link href="/" className="text-xl font-bold hover:opacity-80 transition-opacity">
              {t('common.fileExplorer')}
            </Link>
          </div>

          {/* Center: Search Bar */}
          <div className="flex-1 flex justify-center px-4">
            <div className="w-full max-w-2xl">
              <GlobalSearch />
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex-shrink-0 flex items-center gap-2">
            <ThemeSwitcher />
            <LanguageSwitcher />
            <div className="dropdown dropdown-end" data-testid="user-menu-dropdown">
              <div tabIndex={0} role="button" className="btn btn-ghost btn-circle" data-testid="user-menu-button">
                <UserAvatar user={user} size="md" showTooltip={false} />
              </div>
              <ul
                tabIndex={0}
                className="menu menu-sm dropdown-content bg-base-100 rounded-box z-[1] mt-3 w-52 p-2 shadow"
              >
                <li className="menu-title">
                  <span>{displayName}</span>
                </li>
                <li>
                  <a onClick={handleSignOut} data-testid="sign-out-button">{t('auth.signOut')}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with padding to account for fixed navbar */}
      <main className="mt-16 overflow-auto pb-2" style={{ height: 'calc(100vh - 4rem)', scrollbarGutter: 'stable' }}>
        {children}
      </main>
    </div>
  )
}
